import React, { lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AppProvider, useApp } from "./contexts/AppContext";
import { GentleReminderBanner } from "./components/GentleReminderBanner";
import { SoftErrorBoundary } from "./components/SoftErrorBoundary";
import { SoftSuspense } from "./components/SoftSuspense";
import { MaintenanceGate } from "./components/MaintenanceGate";

// Eager — first paint critical
import Landing from "./pages/Landing";
import AuthPage from "./pages/AuthPage";
import Onboarding from "./pages/Onboarding";
import ParentDashboard from "./pages/ParentDashboard";

// Lazy — non-critical / behind navigation
const ChildProfiles = lazy(() => import("./pages/ChildProfiles"));
const ChildHome = lazy(() => import("./pages/ChildHome"));
const LearningCategory = lazy(() => import("./pages/LearningCategory"));
const Bedtime = lazy(() => import("./pages/Bedtime"));
const Emotions = lazy(() => import("./pages/Emotions"));
const Garden = lazy(() => import("./pages/Garden"));
const Settings = lazy(() => import("./pages/Settings"));
const Membership = lazy(() => import("./pages/Membership"));
const MembershipSuccess = lazy(() => import("./pages/MembershipSuccess"));
const GiftStory = lazy(() => import("./pages/GiftStory"));
const HiveReset = lazy(() => import("./pages/HiveReset"));
const Rhythms = lazy(() => import("./pages/Rhythms"));
const Letters = lazy(() => import("./pages/Letters"));
const LetterDetail = lazy(() => import("./pages/LetterDetail"));
const CalmSounds = lazy(() => import("./pages/CalmSounds"));
const Pause = lazy(() => import("./pages/Pause"));
const Discover = lazy(() => import("./pages/Discover"));
const Discoveries = lazy(() => import("./pages/Discoveries"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));

const Protected = ({ children }) => {
  const { parent, loading } = useApp();
  if (loading) return <div className="min-h-screen bn-honeycomb" />;
  if (!parent) return <Navigate to="/auth" replace />;
  if (!parent.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
};

const Bare = ({ children }) => {
  const { loading } = useApp();
  if (loading) return <div className="min-h-screen bn-honeycomb" />;
  return children;
};

function App() {
  return (
    <div className="App">
      <SoftErrorBoundary>
        <MaintenanceGate>
          <AppProvider>
          <BrowserRouter>
            <SoftSuspense>
              <Routes>
                <Route path="/" element={<Bare><Landing /></Bare>} />
                <Route path="/auth" element={<Bare><AuthPage /></Bare>} />
                <Route path="/auth/forgot" element={<Bare><ForgotPassword /></Bare>} />
                <Route path="/auth/reset" element={<Bare><ResetPassword /></Bare>} />
                <Route path="/onboarding" element={<Bare><Onboarding /></Bare>} />
                <Route path="/dashboard" element={<Protected><ParentDashboard /></Protected>} />
                <Route path="/profiles" element={<Protected><ChildProfiles /></Protected>} />
                <Route path="/child/:childId" element={<Protected><ChildHome /></Protected>} />
                <Route path="/child/:childId/learn/:categoryId" element={<Protected><LearningCategory /></Protected>} />
                <Route path="/child/:childId/bedtime" element={<Protected><Bedtime /></Protected>} />
                <Route path="/child/:childId/hive-reset" element={<Protected><HiveReset /></Protected>} />
                <Route path="/child/:childId/emotions" element={<Protected><Emotions /></Protected>} />
                <Route path="/child/:childId/garden" element={<Protected><Garden /></Protected>} />
                <Route path="/child/:childId/rhythms" element={<Protected><Rhythms /></Protected>} />
                <Route path="/child/:childId/sounds" element={<Protected><CalmSounds /></Protected>} />
                <Route path="/child/:childId/discover" element={<Protected><Discover /></Protected>} />
                <Route path="/child/:childId/discoveries" element={<Protected><Discoveries /></Protected>} />
                <Route path="/settings" element={<Protected><Settings /></Protected>} />
                <Route path="/membership" element={<Protected><Membership /></Protected>} />
                <Route path="/membership/success" element={<Protected><MembershipSuccess /></Protected>} />
                <Route path="/letters" element={<Protected><Letters /></Protected>} />
                <Route path="/letters/:letterId" element={<Protected><LetterDetail /></Protected>} />
                <Route path="/pause" element={<Protected><Pause /></Protected>} />
                <Route path="/gift/:code" element={<Bare><GiftStory /></Bare>} />
                <Route path="/privacy" element={<Bare><Privacy /></Bare>} />
                <Route path="/terms" element={<Bare><Terms /></Bare>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </SoftSuspense>
            <GentleReminderBanner />
            <Toaster position="top-center" />
          </BrowserRouter>
        </AppProvider>
        </MaintenanceGate>
      </SoftErrorBoundary>
    </div>
  );
}

export default App;
